import { values } from 'lodash'
import * as moment from 'moment'

import {
  AU_ZIPCODE_PATTERNS,
  CA_ZIPCODE_PATTERNS,
  JP_ZIPCODE_PATTERNS,
  US_ZIPCODE_PATTERNS,
} from '../constants'
import t from './validatorStrings'

/* tslint:disable-next-line */
const EMAIL_TEST = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const ALL_NON_DIGITS = /\D/g

export function formatNumberOnly(str) {
  return (str || '').replace(ALL_NON_DIGITS, '')
}

export function isValidPhoneNumber(phoneNumber = '') {
  const phoneNumberJustDigits: string = formatNumberOnly(phoneNumber)
  const { length } = phoneNumberJustDigits
  return (
    length === 9 ||
    length === 10 ||
    (length === 11 && phoneNumber[0] === '1') ||
    (phoneNumber[0] === '+' && length >= 11)
  )
}

export function isValidEmail(email = '') {
  return EMAIL_TEST.test(email)
}

export function isValidZipCode(str = '') {
  return (
    US_ZIPCODE_PATTERNS.test(str) ||
    CA_ZIPCODE_PATTERNS.test(str.replace(/\W+/g, '')) ||
    AU_ZIPCODE_PATTERNS.test(str) ||
    JP_ZIPCODE_PATTERNS.test(str)
  )
}

export const validators = {
  email: value => {
    if (!value || value.length === 0) {
      return t.emailNull()
    }
    if (!isValidEmail(value)) {
      return t.emailInvalid()
    }
    return null
  },
  phoneNumber: value => {
    if (!value || value.length === 0) {
      return t.phoneNumberInvalid()
    }
    if (!isValidPhoneNumber(value)) {
      return t.phoneNumberInvalid()
    }
    return null
  },
  optionalPhoneNumber: value => {
    if (value && value.length > 0 && !isValidPhoneNumber(value)) {
      return t.optionalPhoneNumberInvalid()
    }
    return null
  },
  zipCode: value => {
    if (!value || value.length === 0) {
      return t.zipCodeNull()
    }
    if (!isValidZipCode(value)) {
      return t.zipCodeInvalid()
    }
    return null
  },
  legalTextCheckBox: value => {
    if (!value) {
      return t.legalTextUnchecked()
    }
    return null
  },
  firstName: value => {
    if (!value || value.length === 0) {
      return t.firstNameNull()
    }
    if (!value || value.length < 2) {
      return t.firstNameInvalidLength()
    }
    return null
  },
  shortFirstName: value => {
    if (!value || values.length === 0) {
      return t.firstNameNull()
    }
    return null
  },
  middleName: value => {
    if (!value || value.length === 0) {
      return t.middleNameNull()
    }
    return null
  },
  lastName: value => {
    if (!value || value.length === 0) {
      return t.lastNameNull()
    }
    if (!value || value.length < 2) {
      return t.lastNameInvalidLength()
    }
    return null
  },
  shortLastName: value => {
    if (!value || value.length === 0) {
      return t.lastNameNull()
    }
    return null
  },
  password: value => {
    if (!value) {
      return t.passwordNull()
    }
    if (value.length < 8) {
      return t.passwordInvalidLength()
    }
    return null
  },
  licenseNumber: value => {
    if (!value || value.length === 0) {
      return t.licenseNumberNull()
    }
    return null
  },
  licenseState: value => {
    if (!value || value.length === 0) {
      return t.licenseStateNull()
    }
    return null
  },
  vehicleType: value => {
    if (!value || value.length === 0 || value <= 0) {
      return t.vehicleTypeNull()
    }
    return null
  },
  insuranceCheck: value => {
    if (!value) {
      return t.insuranceCheckNull()
    }
    return null
  },
  ssn: value => {
    if (!value || value.length < 9) {
      return t.ssnInvalid()
    }
    return null
  },
  radioButtonGroup: value => {
    if (value === undefined) {
      return t.backgroundCheckCitizenship()
    }
    return null
  },
  birthday: value => {
    // At least 8 chars because 1-1-2000 should be valid
    // as well as 1-01-2000 and 01-1-2000
    if (!value || value.length < 8) {
      return t.birthdayInvalid()
    }

    const minBirthday = moment()
      .subtract(18, 'years')
      .format('YYYY-MM-DD')
    const minDate = moment()
      .subtract(120, 'years')
      .format('YYYY-MM-DD')

    // https://github.com/moment/moment/issues/5232
    // Months are 0 indexed, 0-11
    const dob = value.split('-')
    const birthdate = moment({
      year: dob[0],
      month: dob[1] - 1,
      day: dob[2],
    }).format('YYYY-MM-DD')

    if (birthdate < minDate || birthdate > moment().format('YYYY-MM-DD')) {
      return t.birthdayInvalidDate()
    }

    if (birthdate > minBirthday) {
      return t.birthdayInvalidMinDate()
    }

    return null
  },
  requestedBgCheckCopy: () => {
    // Optional field
    return null
  },
  redCardLastFour: value => {
    if (!value) {
      return t.redCardLastFourNull()
    }

    if (value.length !== 4) {
      return t.redCardLastFourInvalidLength()
    }

    return null
  },
  redCardDelightNumber: value => {
    if (!value) {
      return t.redCardDelightNumberNull()
    }

    if (value.length < 5 || value.length > 10) {
      return t.redCardDelightNumberInvalidLength()
    }

    return null
  },
  hidden: () => {
    // For hidden fields
    return null
  },
  australianBusinessNumber: value => {
    if (!value || value.length < 11) {
      return t.abnInvalid()
    }
    return null
  },
  isGstRegistered: () => {
    // Optional field
    return null
  },

  // TODO:: This currently only supports US,
  // need to move stripe validation into forms

  isValidBankAccountNumber: value => {
    if (!value) {
      return 'Please enter a valid Account Number'
    }
    if (value.length !== 12) {
      return 'Account number must have 12 digits'
    }
    return null
  },
  isValidBankRoutingNumber: value => {
    if (!value) {
      return 'Please enter a valid Routing Number'
    }
    if (value.length !== 9) {
      return 'Routing number must have 9 digits'
    }
    return null
  },
  vehicle: {
    make: value => (!value || value.length === 0 ? t.makeError() : null),
    model: value => (!value || value.length === 0 ? t.modelError() : null),
    color: value => (!value || value.length === 0 ? t.colorError() : null),
  },
}
